import React, {useState} from 'react';

import {useI18n} from '@udemy/i18n';
import {Button} from '@udemy/react-core-components';

import {getReminderTimes, triggerIcsDownload} from '../api/calendar-export';
import {AT_RISK_STEP_ID, STEP_PROGRESS} from '../constants';
import {AT_RISK_HELP_CTA} from '../events';
import {useGoogleCalendarAuth} from '../hooks/use-google-calendar-auth';
import {useLearningReminder} from '../hooks/use-learning-reminder';
import {StepLayout} from '../step-layout.react-component';
import {TiredStepCalendarSync} from './tired-step-calendar-sync.react-component';
import {TiredStepTimeSelection} from './tired-step-time-selection.react-component';

import type {StepRendererProps} from '../types';
import type {TimeOption, TimeOptionId} from './tired-step-time-selection.react-component';

export function TiredStep({
    dispatch,
    onClose,
    onTrackEventClick,
    lastCourseUrl,
    lastCourseId,
}: StepRendererProps) {
    const {gettext} = useI18n();
    const [subState, setSubState] = useState<'time-selection' | 'calendar-sync'>('time-selection');
    const [selectedTimeOption, setSelectedTimeOption] = useState<TimeOptionId | null>(null);

    const googleAuth = useGoogleCalendarAuth();
    const learningReminder = useLearningReminder();

    const timeOptions: TimeOption[] = [
        {
            id: 'tomorrow-morning',
            label: gettext('Tomorrow morning'),
            confirmationText: gettext("We'll remind you tomorrow at 9:00 AM"),
            daysOffset: 1,
            hour: 9,
        },
        {
            id: 'tomorrow-evening',
            label: gettext('Tomorrow evening'),
            confirmationText: gettext("We'll remind you tomorrow at 6:00 PM"),
            daysOffset: 1,
            hour: 18,
        },
        {
            id: 'in-2-days',
            label: gettext('In 2 days'),
            confirmationText: gettext("We'll remind you in 2 days at 9:00 AM"),
            daysOffset: 2,
            hour: 9,
        },
    ];

    const handleConfirm = async () => {
        if (!selectedTimeOption) return;
        const option = timeOptions.find((o) => o.id === selectedTimeOption);
        if (!option) return;

        const {start, end} = getReminderTimes(option.daysOffset, option.hour);
        const created = await learningReminder.createReminder({
            title: gettext('Learning reminder'),
            start,
            end,
            courseId: lastCourseId,
            courseUrl: lastCourseUrl,
        });
        if (created) {
            setSubState('calendar-sync');
            onTrackEventClick(AT_RISK_HELP_CTA.CONFIRM_REMINDER);
        }
    };

    const handleDone = () => {
        onTrackEventClick(AT_RISK_HELP_CTA.DONE);
        onClose();
    };

    const handleGoogleCalendar = async () => {
        const success = await learningReminder.syncToGoogleCalendar(googleAuth.requestAccessToken);
        if (success) onClose();
    };

    const handleIcsDownload = () => {
        if (learningReminder.reminder) triggerIcsDownload(learningReminder.reminder);
    };

    if (subState === 'calendar-sync') {
        return (
            <StepLayout
                progressValue={STEP_PROGRESS[AT_RISK_STEP_ID.TIRED]}
                onClose={onClose}
                footerRightChildren={
                    <Button udStyle="primary" size="medium" onClick={handleDone}>
                        {gettext('Done')}
                    </Button>
                }
            >
                <TiredStepCalendarSync
                    isGoogleAvailable={googleAuth.isAvailable}
                    isGoogleLoading={learningReminder.isSyncing}
                    error={learningReminder.error}
                    onGoogleClick={handleGoogleCalendar}
                    onIcsDownload={handleIcsDownload}
                />
            </StepLayout>
        );
    }

    return (
        <StepLayout
            progressValue={STEP_PROGRESS[AT_RISK_STEP_ID.TIRED]}
            onClose={onClose}
            footerLeftChildren={
                <Button
                    udStyle="ghost"
                    size="medium"
                    onClick={() =>
                        dispatch({type: 'STEP_ADVANCED', nextStepId: AT_RISK_STEP_ID.INITIAL})
                    }
                >
                    {gettext('Back')}
                </Button>
            }
            footerRightChildren={
                <Button
                    udStyle="primary"
                    size="medium"
                    disabled={!selectedTimeOption || learningReminder.isCreating}
                    onClick={handleConfirm}
                >
                    {learningReminder.isCreating
                        ? gettext('Creating…')
                        : gettext('Confirm reminder')}
                </Button>
            }
        >
            <TiredStepTimeSelection
                timeOptions={timeOptions}
                selectedTimeOptionId={selectedTimeOption}
                onSelect={setSelectedTimeOption}
                error={learningReminder.error}
            />
        </StepLayout>
    );
}
